import { FitText } from '../../components/FitText.js';
import { useT } from '../../i18n/index.js';
import { SUPPORT_CHAT } from '../../lib/support.js';
import { openExternal } from '../../services/telegram.js';

/**
 * What an account sees when another account holds its network (one account per
 * network, server: modules/auth/ipgate.ts). Not a ban: it gets in as soon as
 * it changes network, or once the network is free again. Same scene as the ban
 * screen, with its own title set on the empty gold plate.
 */
export function IpInUseScreen({
  details,
  onRetry,
  variant = 'in-use',
}: {
  details: unknown;
  onRetry: () => void;
  /** in-use: another account holds the network; banned: the network was blocked by hand in the panel. */
  variant?: 'in-use' | 'banned';
}) {
  const { t } = useT();
  const hours = Number((details as { windowHours?: unknown } | null)?.windowHours) || 24;
  const banned = variant === 'banned';
  const title = banned ? t('ip.bannedTitle') : t('ip.title');

  return (
    <div className="ban" role="alert">
      <div className="ban__frame">
        <div className="ban__stage">
          <h1 className="visually-hidden">{title}</h1>
          <div className="ban__plate ban__plate--live" aria-hidden>
            <FitText className="ban__plate-text">{title}</FitText>
          </div>
        </div>

        <section className="ban__panel">
          <p className="ban__lead">{banned ? t('ip.bannedLead') : t('ip.lead')}</p>
          <p className="ban__why">{banned ? t('ip.bannedBody') : t('ip.body')}</p>
          <figure className="ban__rule">
            <figcaption className="ban__rule-label">{t('ip.ruleLabel')}</figcaption>
            <blockquote className="ban__rule-text">{banned ? t('ip.bannedHint') : t('ip.hint', { hours })}</blockquote>
          </figure>
        </section>

        <div className="ban__actions">
          <button type="button" className="ban__primary" onClick={onRetry}>
            <FitText as="span" className="ban__primary-text">
              {t('ip.retry')}
            </FitText>
          </button>
          <button type="button" className="ban__terms-toggle" onClick={() => openExternal(SUPPORT_CHAT)}>
            {t('ban.support')}
          </button>
        </div>
      </div>
    </div>
  );
}
